import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Users, Heart, ArrowRight, Handshake, Target, Gift, UtensilsCrossed, UserPlus, CheckCircle, Sparkles } from "lucide-react";
import Marquee from "react-fast-marquee";

const Programs = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = "Programs | Soar Solutions Youth Center";
    if (location.hash) {
      const elementId = location.hash.substring(1);
      const el = document.getElementById(elementId);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [location.hash]);

  return (
    <div data-testid="programs-page" className="bg-white">

      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_98cd7b09-1d25-49bf-8206-01ee668f439d/artifacts/67pjtzmq_Screenshot%202026-01-23%20091226.png"
            alt="Youth Programs"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/90 to-soar-blue/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl space-y-6">
            <span className="inline-block border border-soar-gold/40 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-gold uppercase opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
              Our Programs
            </span>
            <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tight text-white opacity-0 animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
              Transformative Programs for <span className="text-soar-gold">Youth</span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl opacity-0 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
              Our programs are designed to meet young people where they are and help them grow into confident,
              capable adults ready to make their mark on the world.
            </p>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-5 bg-[#212163]">
        <Marquee speed={60} gradient={false}>
          {["EMPOWER", "ELEVATE", "SOAR", "MENTOR", "LEAD", "DREAM"].map((w) => (
            <span key={w} className="font-heading text-4xl text-[#ddb64d]/20 mx-12 tracking-wider">{w}</span>
          ))}
        </Marquee>
      </div>

      {/* Think For You */}
      <section id="think-for-you" className="py-24 bg-white" data-testid="think-for-you">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs border border-soar-teal/30 text-soar-teal px-3 py-1 rounded-full font-semibold">Growth</span>
                <span className="text-xs border border-soar-teal/30 text-soar-teal px-3 py-1 rounded-full font-semibold">Empowerment</span>
                <span className="text-xs border border-soar-teal/30 text-soar-teal px-3 py-1 rounded-full font-semibold">Guidance</span>
              </div>
              <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight">Think For You</h2>
              <p className="font-accent text-3xl text-soar-gold">Mentorship Program</p>
              <p className="text-soar-blue/60 leading-relaxed text-lg">
                Our mentorship program empowers youth (ages 13-25) to think critically, speak confidently,
                and take ownership of their future. We guide them in building their dreams, refining their
                goals, and even reimagining their possibilities.
              </p>
              <div className="card-elevated rounded-2xl p-6">
                <p className="font-heading text-soar-blue mb-1">Prepare to change for the better</p>
                <p className="text-soar-blue/50 text-sm">Try our real life guidance from people who've been where you are!</p>
              </div>
              <Link to="/mentorship-form">
                <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                  Apply for Mentorship <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img src="https://customer-assets.emergentagent.com/job_98cd7b09-1d25-49bf-8206-01ee668f439d/artifacts/67pjtzmq_Screenshot%202026-01-23%20091226.png" alt="Think For You Mentorship" className="rounded-3xl w-full" />
              <div className="absolute -bottom-5 -left-5 bg-soar-gold text-[#0a0a2e] rounded-2xl p-5 shadow-lg">
                <p className="font-heading text-3xl">50+</p>
                <p className="text-sm font-medium">Youth Served</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Hands on Deck */}
      <section id="volunteer" className="py-24 bg-white" data-testid="all-hands-on-deck">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <img src="https://customer-assets.emergentagent.com/job_98cd7b09-1d25-49bf-8206-01ee668f439d/artifacts/d8zhrd1m_Screenshot%202026-01-23%20091126.png" alt="All Hands on Deck" className="rounded-3xl w-full" />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs border border-soar-gold/30 text-soar-gold px-3 py-1 rounded-full font-semibold">Serve</span>
                <span className="text-xs border border-soar-gold/30 text-soar-gold px-3 py-1 rounded-full font-semibold">Lead</span>
                <span className="text-xs border border-soar-gold/30 text-soar-gold px-3 py-1 rounded-full font-semibold">Empower</span>
              </div>
              <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight">All Hands on Deck</h2>
              <p className="font-accent text-3xl text-soar-gold">Volunteer Program</p>
              <p className="text-soar-blue/60 leading-relaxed text-lg">
                Teaching our youth life skills and the importance of seeing a need and filling a need
                within our community. Each experience empowers young people to think beyond themselves,
                take action, and build meaningful connections through service.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {["Teach Life Skills", "Community Awareness", "Critical Thinking", "Builds Confidence", "Leadership"].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-soar-teal flex-shrink-0" aria-hidden="true" />
                    <span className="text-sm text-soar-blue/50">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/volunteer-form">
                <Button className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-[#0a0a2e] px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                  Become a Volunteer <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feed The Deserved */}
      <section id="feed-the-deserved" className="py-24 bg-white" data-testid="feed-the-deserved">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs border border-soar-teal/30 text-soar-teal px-3 py-1 rounded-full font-semibold">Community</span>
                <span className="text-xs border border-soar-teal/30 text-soar-teal px-3 py-1 rounded-full font-semibold">Compassion</span>
              </div>
              <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight">Feed The Deserved</h2>
              <p className="text-soar-blue/60 leading-relaxed text-lg">
                We teach youth the value of decision-making by involving them in serving hot Thanksgiving meals
                to houseless community members. We collaborate with local restaurants and businesses to provide
                meals, fostering love, building community connections, and empowering youth to give back.
              </p>
              <div className="card-elevated rounded-2xl p-6">
                <p className="font-heading text-5xl text-soar-teal tracking-tight">903</p>
                <p className="text-soar-blue/50 text-sm">Community members fed through this program</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  { icon: UtensilsCrossed, text: "Hot meals served" },
                  { icon: Users, text: "Community leadership" },
                  { icon: Heart, text: "Empathy through action" },
                  { icon: Target, text: "Social awareness" },
                ].map((d) => (
                  <div key={d.text} className="flex items-center gap-2 text-soar-blue/50">
                    <d.icon className="w-4 h-4 text-soar-teal flex-shrink-0" aria-hidden="true" /> {d.text}
                  </div>
                ))}
              </div>
            </div>
            <img src="https://customer-assets.emergentagent.com/job_98cd7b09-1d25-49bf-8206-01ee668f439d/artifacts/kl7p84mp_Screenshot%202026-01-23%20090233.png" alt="Feed The Deserved" className="rounded-3xl w-full" />
          </div>
        </div>
      </section>

      {/* Fresh Start */}
      <section id="fresh-start" className="py-24 bg-white" data-testid="fresh-start">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <img src="https://customer-assets.emergentagent.com/job_98cd7b09-1d25-49bf-8206-01ee668f439d/artifacts/ti4qf9qv_Screenshot%202026-01-23%20091001.png" alt="Fresh Start" className="rounded-3xl w-full" />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs border border-soar-gold/30 text-soar-gold px-3 py-1 rounded-full font-semibold">Dignity</span>
                <span className="text-xs border border-soar-gold/30 text-soar-gold px-3 py-1 rounded-full font-semibold">Confidence</span>
              </div>
              <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight">Fresh Start</h2>
              <p className="font-accent text-3xl text-soar-gold">School Gear for the School Year</p>
              <p className="text-soar-blue/60 leading-relaxed text-lg">
                We teamed up with Macy's and Upside Academy, Inc. to create Fresh Start: School Gear for
                the School Year, a shopping experience empowering underserved, foster, and justice-involved
                youth to choose their own clothing and supplies.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/partners">
                  <Button className="rounded-full bg-soar-blue/10 hover:bg-soar-blue/15 border border-soar-blue/20 text-soar-blue px-6 py-5 font-bold hover:scale-105 transition-transform duration-200">
                    Partner With Us <Handshake className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/donate">
                  <Button className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-[#0a0a2e] px-6 py-5 font-bold hover:scale-105 transition-transform duration-200">
                    <Heart className="w-4 h-4 mr-2" /> Sponsor a Youth
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Adopt A Youth */}
      <section id="adopt-a-youth" className="py-24 bg-white" data-testid="adopt-a-youth">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs border border-soar-teal/30 text-soar-teal px-3 py-1 rounded-full font-semibold">Hope</span>
                <span className="text-xs border border-soar-teal/30 text-soar-teal px-3 py-1 rounded-full font-semibold">Generosity</span>
              </div>
              <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight">Adopt A Youth</h2>
              <p className="text-soar-blue/60 leading-relaxed text-lg">
                We collaborate with foster youth facilities where community donors "adopt" a youth by
                fulfilling items from their wish list, ensuring each youth feels seen and valued.
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  { icon: Gift, text: "Wish list fulfillment" },
                  { icon: Heart, text: "Sense of belonging" },
                  { icon: Users, text: "Community connection" },
                  { icon: Sparkles, text: "Holiday joy & cheer" },
                ].map((d) => (
                  <div key={d.text} className="flex items-center gap-2 text-soar-blue/50">
                    <d.icon className="w-4 h-4 text-soar-teal flex-shrink-0" aria-hidden="true" /> {d.text}
                  </div>
                ))}
              </div>
              <Link to="/donate">
                <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                  <Gift className="w-5 h-5 mr-2" /> Adopt a Youth
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <img src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/jgwi2ynu_IMG_0036.jpeg" alt="Adopt A Youth" className="w-full h-56 rounded-2xl object-cover" />
              <img src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/5g8o2ot5_IMG_0028.jpeg" alt="Gift wrapping" className="w-full h-56 rounded-2xl object-cover" />
              <img src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/u5rk6o7d_IMG_0027.jpeg" alt="Youth volunteer" className="w-full h-56 rounded-2xl object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Referral CTA */}
      <section className="py-24 relative overflow-hidden" data-testid="referral-cta">
        <div className="absolute inset-0 bg-soar-teal" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <UserPlus className="w-12 h-12 mx-auto mb-4 text-white/80" aria-hidden="true" />
          <h2 className="font-heading text-4xl md:text-5xl text-soar-blue tracking-tight mb-4">Know a Youth Who Needs Support?</h2>
          <p className="text-soar-blue/80 mb-8 max-w-2xl mx-auto text-lg">
            If you know a young person aged 13-25 who could benefit from our programs, refer them to us.
          </p>
          <Link to="/referral-form">
            <Button className="rounded-full bg-white text-soar-teal hover:bg-white/90 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
              Refer a Youth <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-white" data-testid="programs-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-5xl md:text-6xl text-soar-blue tracking-tight mb-6">Ready to Join a Program?</h2>
          <p className="text-soar-blue/50 mb-12 max-w-2xl mx-auto text-lg">
            Take the first step towards your goals. Apply for mentorship, sign up as a volunteer,
            or partner with us to make an impact.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/mentorship-form" data-testid="programs-mentorship-btn">
              <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                Apply for Mentorship
              </Button>
            </Link>
            <Link to="/volunteer-form" data-testid="programs-volunteer-btn">
              <Button className="rounded-full bg-soar-blue/10 hover:bg-soar-blue/15 border border-soar-blue/20 text-soar-blue px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                Become a Volunteer
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
